
export const insertMarkdown = (
  textarea: HTMLTextAreaElement | null,
  prefix: string,
  suffix: string
): string | null => {
  if (!textarea) return null;

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const text = textarea.value;
  const before = text.substring(0, start);
  const selected = text.substring(start, end);
  const after = text.substring(end);

  const newText = `${before}${prefix}${selected}${suffix}${after}`;

  return newText;
};

// Import pattern system
import { applyAllPatterns, applyPatternsInMathZone, UNICODE_TO_LATEX, UNICODE_TO_LATEX_AUTOCONVERT, isNotebookPatternsEnabled } from './mathSymbols';

// Helper: Convert range notation (i=1:n) to LaTeX subscript/superscript
const convertRangeNotation = (text: string): string => {
  return text.replace(/_([^_\s]+):([^_\s]+)/g, '_{$1}^{$2}');
};

// Helper: Auto-subscript variable patterns (xi, yi, zn → x_i, y_i, z_n)
const autoSubscriptVariables = (text: string): string => {
  // Pattern: single letter + single letter/digit → letter_subscript
  // Examples: xi → x_i, y1 → y_1, zn → z_n
  return text.replace(/\b([a-z])([a-z0-9])\b/gi, (match, base, subscript) => {
    // Only convert if it looks like a math variable (not a word like "in", "an", "is")
    const commonWords = ['in', 'an', 'is', 'it', 'at', 'on', 'to', 'or', 'if', 'as', 'by'];
    if (commonWords.includes(match.toLowerCase())) {
      return match;
    }
    return `${base}_${subscript}`;
  });
};

// Helper: Smart expression extraction - handles complex math patterns
const extractMathExpression = (symbol: string, rest: string): { expression: string; latex: string } => {
  let expression = '';
  let i = 0;

  // Track nesting level for parentheses/braces
  let parenDepth = 0;
  let braceDepth = 0;

  // Continue while we're in a math expression
  while (i < rest.length) {
    const char = rest[i];

    // Track nesting
    if (char === '(') parenDepth++;
    if (char === ')') parenDepth--;
    if (char === '{') braceDepth++;
    if (char === '}') braceDepth--;

    // Stop conditions
    if (parenDepth < 0 || braceDepth < 0) break; // Unmatched closing bracket
    if (parenDepth === 0 && braceDepth === 0) {
      // If we hit whitespace or end of math expression, stop
      // Allow \ and ^ in math expression
      if (char === ' ' && !/[^_+=\-*/(){}0-9a-zA-Z:,.^\\\s]/.test(rest[i + 1] || '')) {
        break;
      }
    }

    // Include math-like characters
    // BREAK if char is NOT in this list (valid math chars)
    if (/[^_+=\-*/(){}0-9a-zA-Z:,.\s^\|\[\]]/.test(char) && parenDepth === 0 && braceDepth === 0) {
      break;
    }

    expression += char;
    i++;
  }

  // Apply smart conversions
  let latex = expression;
  latex = convertRangeNotation(latex); // _i=1:n → _{i=1}^{n}
  latex = autoSubscriptVariables(latex); // xi → x_i

  return { expression, latex };
};

// Split a string by a single-char separator, respecting nested brackets
const splitBy = (str: string, sep: string): string[] => {
  const parts: string[] = [];
  let depth = 0, cur = '';
  for (const c of str) {
    if ('({['.includes(c)) depth++;
    else if (')}]'.includes(c)) depth--;
    if (c === sep && depth === 0) { parts.push(cur); cur = ''; }
    else cur += c;
  }
  parts.push(cur);
  return parts;
};

// Convert a matrix cell string to raw LaTeX (no $ wrappers)
const cellToLatex = (cell: string): string => {
  let s = cell.trim();
  s = s.replace(/⁻¹/g, '^{-1}');
  s = s.replace(/⁻²/g, '^{-2}');
  s = s.replace(/⁻/g, '^{-}');
  s = s.replace(/ᵀ/g, '^{T}');
  s = s.replace(/²/g, '^{2}');
  s = s.replace(/³/g, '^{3}');
  UNICODE_TO_LATEX.forEach((latex, unicode) => { s = s.split(unicode).join(latex); });
  return s;
};

// Convert mat(a, b; c, d) → $$\begin{pmatrix} a & b \\ c & d \end{pmatrix}$$
const convertMatrixNotation = (text: string): string => {
  let result = '', i = 0;
  while (i < text.length) {
    const idx = text.indexOf('mat(', i);
    if (idx === -1) { result += text.slice(i); break; }
    // Skip if preceded by a word char (e.g. "format(")
    if (idx > 0 && /[a-zA-Z0-9_]/.test(text[idx - 1])) {
      result += text.slice(i, idx + 1); i = idx + 1; continue;
    }
    result += text.slice(i, idx);
    let depth = 1, j = idx + 4;
    while (j < text.length && depth > 0) {
      if (text[j] === '(') depth++;
      else if (text[j] === ')') depth--;
      j++;
    }
    if (depth !== 0) { result += 'mat('; i = idx + 4; continue; }
    const content = text.slice(idx + 4, j - 1);
    const rows = splitBy(content, ';').map(row =>
      splitBy(row, ',').map(cell => cellToLatex(cell)).join(' & ')
    );
    result += `$$\\begin{pmatrix} ${rows.map(r => r.trim()).join(' \\\\ ')} \\end{pmatrix}$$`;
    i = j;
  }
  return result;
};

// A lightweight Markdown parser with KaTeX support
export const parseMarkdown = (text: string): string => {
  if (!text) return '';

  const mathSegments: string[] = [];
  let processed = text;

  // Convert mat(...) notation → LaTeX pmatrix before any other processing
  processed = convertMatrixNotation(processed);

  const extractMath = (regex: RegExp, isDisplay: boolean) => {
    processed = processed.replace(regex, (match, tex) => {
      // @ts-ignore
      if (typeof window !== 'undefined' && window.katex) {
        try {
          // @ts-ignore
          const html = window.katex.renderToString(tex, { displayMode: isDisplay, throwOnError: false });
          if (html && html.trim()) {
            const id = `@@MATH${isDisplay ? 'BLOCK' : 'INLINE'}${mathSegments.length}@@`;
            mathSegments.push(html);
            return id;
          } else {
            return match;
          }
        } catch (e) {
          return match;
        }
      }
      return match;
    });
  };

  // Inline math regex: require non-whitespace at both ends to avoid matching currency ($5)
  const inlineMathRe = /\$([^\s$\n][^$\n]*?[^\s$\n]|[^\s$\n])\$/g;

  const patternsOn = isNotebookPatternsEnabled();

  // Pre-process $$...$$ zones: apply ALL patterns (including mathZoneOnly) inside them
  if (patternsOn) {
    processed = processed.replace(/\$\$([\s\S]*?)\$\$/g, (_match, content) =>
      `$$${applyPatternsInMathZone(content)}$$`
    );
  }
  extractMath(/\$\$([^$]*?)\$\$/g, true);

  // Pre-process $...$ zones the same way so mathZoneOnly patterns work there too
  if (patternsOn) {
    processed = processed.replace(inlineMathRe, (_match, content) =>
      `$${applyPatternsInMathZone(content)}$`
    );
  }
  extractMath(new RegExp(inlineMathRe.source, 'g'), false);

  if (patternsOn) {
    processed = applyAllPatterns(processed);
    extractMath(new RegExp(inlineMathRe.source, 'g'), false);
  }

  // Protect ~~strikethrough~~ before unicode conversion so ~ symbols don't break it.
  // Store raw content in array; restore as <s>...</s> after HTML escaping.
  const strikethroughSegs: string[] = [];
  processed = processed.replace(/~~([^]+?)~~/g, (_match, content) => {
    const id = `@@STRIKE${strikethroughSegs.length}@@`;
    strikethroughSegs.push(content);
    return id;
  });

  // Unescape \~ before UNICODE_TO_LATEX runs — otherwise \~ becomes \$~$ where
  // KaTeX renders ~ as a non-breaking space, leaving a stray backslash visible.
  processed = processed.replace(/\\~/g, '~');

  if (patternsOn) {
    // Use UNICODE_TO_LATEX_AUTOCONVERT which excludes symbols flagged skipAutoConvert:true —
    // those symbols (e.g. ᵀ, °) already render correctly as Unicode and shouldn't be wrapped
    // in $...$ which would change their visual appearance via KaTeX.
    const unicodeMathSymbols = Array.from(UNICODE_TO_LATEX_AUTOCONVERT.keys()).join('');
    if (unicodeMathSymbols) {
      const symbolRegex = new RegExp(`([${unicodeMathSymbols}])`, 'g');
      processed = processed.replace(symbolRegex, (match, symbol) => {
        const latex = UNICODE_TO_LATEX_AUTOCONVERT.get(symbol);
        return latex ? `$${latex}$` : match;
      });
    }
    extractMath(new RegExp(inlineMathRe.source, 'g'), false);
  }

  // Preserve TipTap hard line break markers (\<newline>) as a placeholder so they survive
  // the \n → space conversion below and still render as <br /> in view mode.
  // Plain single \n (e.g. from SimpleMind imports) are soft breaks and become spaces.
  processed = processed.replace(/\\\n/g, '@@HARDBREAK@@');
  processed = processed.replace(/^\\\s*$/gm, '');

  // Unescape TipTap-escaped markdown characters (e.g. \[ \] \( \) )
  processed = processed.replace(/\\([\[\]()*_`~])/g, '$1');

  // Collapse TipTap blank-paragraph markers: sequences of newlines with only
  // spaces/NBSP between them (from preProcessMarkdown and BlankLineParagraph)
  // collapse to a single paragraph break so view mode doesn't show giant gaps.
  processed = processed.replace(/\n([  \t]*\n)+/g, '\n\n');

  // Decode HTML entities that TipTap may have stored (tiptap-markdown html:false mode
  // escapes >, <, & to entities). Must decode before our own HTML escaping to avoid
  // double-encoding already-corrupted stored content (e.g. &gt; → &amp;gt;).
  processed = processed
    .replace(/&amp;/g, '&')
    .replace(/&gt;/g, '>')
    .replace(/&lt;/g, '<')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");

  // Extract blockquotes before HTML escaping so > isn't turned into &gt;
  const blockquoteSegs: string[] = [];
  processed = processed.replace(/((?:^> [^\n]*\n?)+)/gm, (match) => {
    const id = `@@BLOCKQUOTE${blockquoteSegs.length}@@`;
    blockquoteSegs.push(match.replace(/^> /gm, '').trim());
    return id + '\n';
  });

  processed = processed
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  processed = processed.replace(/`([^`\n]+)`/g, '<code class="font-mono text-sm bg-gray-100 dark:bg-slate-800 px-1 rounded">$1</code>');

  // Allow bold/italic to span a single soft line break (e.g. SimpleMind imports wrap
  // long lines mid-bold). (?:[^\n]|\n(?!\n)) matches any char OR a lone \n that is
  // not followed by another \n (i.e. not a paragraph break).
  processed = processed.replace(/\*\*((?:[^\n]|\n(?!\n))+?)\*\*/g, '<strong>$1</strong>');

  // Single * italic intentionally disabled — * is used as math notation (x*, λ*, optimal markers).

  // Restore strikethrough placeholders after HTML escaping so <s> tag isn't double-escaped.
  // Apply HTML escaping + bold/italic to the stored raw content.
  if (strikethroughSegs.length > 0) {
    processed = processed.replace(/@@STRIKE(\d+)@@/g, (_match, idx) => {
      let c = strikethroughSegs[parseInt(idx)];
      c = c.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      c = c.replace(/\*\*((?:[^\n]|\n(?!\n))+?)\*\*/g, '<strong>$1</strong>');
      return `<s>${c}</s>`;
    });
  }

  processed = processed.replace(/^### (.+)$/gm, '<h3 class="text-lg font-semibold mt-3 mb-1 text-gray-900 dark:text-gray-100">$1</h3>');
  processed = processed.replace(/^## (.+)$/gm, '<h2 class="text-xl font-bold mt-4 mb-2 text-gray-900 dark:text-gray-100">$1</h2>');
  processed = processed.replace(/^# (.+)$/gm, '<h1 class="text-2xl font-bold mt-4 mb-2 text-gray-900 dark:text-gray-100">$1</h1>');

  processed = processed.replace(/^[-*]\s+(.*)$/gm, '<li>$1</li>');
  processed = processed.replace(/(<li>.*<\/li>(\n|$))+/g, (match) => {
    return `<ul class="list-disc pl-5 mb-2">${match}</ul>`;
  });

  processed = processed.replace(/^\d+\.\s+(.*)$/gm, '<li class="@@ol">$1</li>');
  processed = processed.replace(/(<li class="@@ol">.*<\/li>(\n|$))+/g, (match) => {
    return `<ol class="list-decimal pl-5 mb-2">${match.replace(/ class="@@ol"/g, '')}</ol>`;
  });

  // Restore blockquotes with HTML escaping + inline formatting applied
  if (blockquoteSegs.length > 0) {
    processed = processed.replace(/@@BLOCKQUOTE(\d+)@@/g, (_match, idx) => {
      let c = blockquoteSegs[parseInt(idx)];
      c = c.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      c = c.replace(/\*\*((?:[^\n]|\n(?!\n))+?)\*\*/g, '<strong>$1</strong>');
      c = c.replace(/\n/g, '<br />');
      return `<blockquote class="guidance-card" style="background:rgba(251,191,36,0.08);border-left:3px solid rgba(245,158,11,0.45);border-radius:0 10px 10px 0;padding:10px 14px;margin:8px 0;font-style:normal;">${c}</blockquote>`;
    });
  }

  // Horizontal rule: --- (3+ dashes alone on a line)
  processed = processed.replace(/^-{3,}$/gm, '<hr class="my-4 border-t border-gray-200 dark:border-slate-600" />');

  // Collapse 3+ consecutive newlines to 2
  processed = processed.replace(/\n{3,}/g, '\n\n');

  // \n\n = paragraph break → two <br /> (visual gap between paragraphs)
  // single \n = soft wrap from import (SimpleMind etc.) → space, not a line break
  // @@HARDBREAK@@ = explicit TipTap Shift+Enter hard break → <br />
  processed = processed.replace(/\n\n/g, '<br /><br />');
  processed = processed.replace(/\n/g, ' ');
  processed = processed.replace(/@@HARDBREAK@@/g, '<br />');

  processed = processed.replace(/@@MATH(BLOCK|INLINE)(\d+)@@/g, (match, type, index) => {
    return mathSegments[parseInt(index)] || match;
  });

  return processed;
};

/**
 * Post-process a mounted DOM element: find any $...$ or $$...$$ that
 * parseMarkdown left as raw text (e.g. when window.katex was not yet ready)
 * and render them with KaTeX auto-render.
 */
export const applyKatexToElement = (el: HTMLElement | null): void => {
  if (!el) return;
  // @ts-ignore
  if (typeof window === 'undefined' || !window.renderMathInElement) return;
  try {
    // @ts-ignore
    window.renderMathInElement(el, {
      delimiters: [
        { left: '$$', right: '$$', display: true },
        { left: '$', right: '$', display: false },
      ],
      throwOnError: false,
    });
  } catch { /* ignore */ }
};

// Reflow text: join single line breaks within a paragraph into spaces,
// preserving paragraph breaks (double newlines) and list/header lines.
export const reflowText = (text: string): string => {
  if (!text) return '';
  const paragraphs = text.split(/\n\s*\n/);
  return paragraphs.map(p => {
    const lines = p.split('\n');
    if (lines.length <= 1) return p;
    const isSpecial = lines.some(line => {
      const t = line.trim();
      return t.startsWith('-') || t.startsWith('*') || t.startsWith('#') || t.startsWith('>') || /^\d+\./.test(t);
    });
    if (isSpecial) return p;
    return lines.map(l => l.trim()).filter(Boolean).join(' ');
  }).join('\n\n');
};
